import type {
  ProfileCompleteness,
  StorefrontPhotoSource,
  StorefrontPhotoVerificationStatus,
  StoreImage,
  StoreProfileDetail,
  StoreProfileListing,
} from '../types';
import type { LiveStoreContext } from './mapLiveStoreContext';
import { applyLiveStoreContext } from './mapLiveStoreContext';
import { deriveTwinReadiness } from './twinReadiness';
import { normalizePhotoFocalPoint } from './photoFocalPoint';

export interface ApiStoreRecord {
  id: number;
  name: string;
  address: string;
  phone?: string | null;
  managerName?: string | null;
  retailerIdentifier?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  region?: string | null;
  retailerId: number;
  formatId?: number | null;
  hasESL?: boolean | null;
  photoUrl?: string | null;
  storefrontPhotoSource?: StorefrontPhotoSource;
  storefrontPhotoVerificationStatus?: StorefrontPhotoVerificationStatus;
  storefrontPhotoSourceUrl?: string | null;
  storefrontPhotoSourcePageUrl?: string | null;
  storefrontPhotoSourceTitle?: string | null;
  /** True when stores.photo_url is set in DB (even if signed URL resolution is pending) */
  hasStorePhoto?: boolean;
  photoFocalX?: number | null;
  photoFocalY?: number | null;
  notes?: string | null;
  archivedAt?: string | null;
  retailerName?: string | null;
  formatName?: string | null;
}

export interface ApiRetailer {
  id: number;
  name: string;
  logo?: string | null;
}

export interface ApiStoreFormat {
  id: number;
  name: string;
  retailerId: number;
}

export interface GalleryImageRecord {
  id: number;
  assetUrl?: string;
  thumbnailUrl?: string;
  storeName?: string;
  campaignName?: string;
  operatorName?: string;
  createdAt: string;
  notes?: string;
  assignmentId?: number | null;
  campaignId?: number | null;
  /** In-store location from task/campaign posting instructions */
  instoreLocationId?: number | null;
}

function completenessFromSignals(hasPhoto: boolean, galleryCount: number): {
  completeness: ProfileCompleteness;
  percent: number;
} {
  if (hasPhoto && galleryCount >= 5) {
    return { completeness: 'partial', percent: 55 };
  }
  if (hasPhoto || galleryCount > 0) {
    return { completeness: 'partial', percent: 35 };
  }
  return { completeness: 'minimal', percent: 12 };
}

export function mapGalleryToStoreImages(images: GalleryImageRecord[]): StoreImage[] {
  return images.map((img) => ({
    id: `gallery-${img.id}`,
    url: img.assetUrl || img.thumbnailUrl || '',
    assetUrl: img.assetUrl ?? null,
    caption: img.campaignName ? `${img.campaignName} — ${img.storeName ?? 'Store'}` : (img.storeName ?? 'Store photo'),
    captureDate: img.createdAt.slice(0, 10),
    surveyId: 'evidence-gallery',
    zone: 'media_location',
    tags: ['posting', 'gallery', img.campaignName ?? 'campaign'].filter(Boolean) as string[],
    notes: img.notes,
    capturedBy: img.operatorName ?? 'Field operator',
    postingInstructionInstoreLocationId: img.instoreLocationId ?? undefined,
    assignmentId: img.assignmentId ?? undefined,
    campaignId: img.campaignId ?? undefined,
  }));
}

/** Listing/header fields come from the REACTOR store record only — never demo scaffold metrics. */
export function buildListingFromStore(
  store: ApiStoreRecord,
  retailerName: string,
  formatName: string | null,
  galleryImageCount = 0,
  retailerLogoUrl: string | null = null,
): StoreProfileListing {
  const hasPhoto = Boolean(store.hasStorePhoto ?? store.photoUrl);
  const completeness = completenessFromSignals(hasPhoto, galleryImageCount);

  return {
    id: String(store.id),
    storeId: store.id,
    storeName: store.name,
    retailer: retailerName,
    retailerId: store.retailerId,
    retailerLogoUrl,
    storeCode: store.retailerIdentifier ?? `STORE-${store.id}`,
    address: store.address,
    region: store.region ?? '—',
    storeFormat: formatName ?? '—',
    floorAreaSqm: null,
    profileCompleteness: completeness.completeness,
    profileCompletenessPercent: completeness.percent,
    lastSurveyDate: null,
    currentCampaignCount: null,
    thumbnailUrl: store.photoUrl ?? null,
    storefrontPhotoSource: store.storefrontPhotoSource ?? (hasPhoto ? 'uploaded' : 'missing'),
    storefrontPhotoVerificationStatus:
      store.storefrontPhotoVerificationStatus ?? (hasPhoto ? 'confirmed' : 'missing'),
    storefrontPhotoSourcePageUrl: store.storefrontPhotoSourcePageUrl ?? null,
    dataSource: 'live',
    isMockData: false,
    hasPhoto,
    galleryImageCount,
  };
}

function buildReactorStoreCore(
  store: ApiStoreRecord,
  retailerName: string,
  formatName: string | null,
  galleryImages: GalleryImageRecord[],
  retailerLogoUrl: string | null = null,
): StoreProfileDetail {
  const listing = buildListingFromStore(store, retailerName, formatName, galleryImages.length, retailerLogoUrl);
  const mappedGalleryImages = mapGalleryToStoreImages(galleryImages);

  return {
    ...listing,
    storeDetails: {
      phone: store.phone ?? null,
      managerName: store.managerName ?? null,
      retailerIdentifier: store.retailerIdentifier ?? null,
      formatId: store.formatId ?? null,
      hasESL: Boolean(store.hasESL),
      notes: store.notes ?? null,
      archivedAt: store.archivedAt ?? null,
    },
    heroImageUrl: store.photoUrl ?? null,
    heroPhotoFocalPoint: normalizePhotoFocalPoint(store.photoFocalX, store.photoFocalY),
    fullAddress: store.address,
    aisleCount: 0,
    storeAreasCount: 0,
    evidenceMappingStats: {
      total: mappedGalleryImages.length,
      mapped: 0,
      needsReview: 0,
      unplaced: mappedGalleryImages.length,
    },
    totalMediaLocations: 0,
    totalMediaCapacity: 0,
    campaignsToDateCount: null,
    coordinates:
      store.latitude != null && store.longitude != null
        ? { lat: store.latitude, lng: store.longitude }
        : null,
    contacts: store.managerName
      ? [{ name: store.managerName, role: 'Store Manager', phone: store.phone ?? undefined }]
      : [],
    openingHours: [],
    accessNotes: store.notes?.trim() || 'No access notes on the store record.',
    parkingNotes: 'Not recorded on the store record.',
    loadingNotes: 'Not recorded on the store record.',
    deliveryNotes: 'Not recorded on the store record.',
    storeDimensions: {
      totalFloorAreaSqm: 0,
      salesAreaSqm: 0,
      ceilingHeightMetres: 0,
      checkoutCount: 0,
    },
    aisles: [],
    floorplanZones: [],
    mediaLocations: [],
    campaigns: [],
    images: mappedGalleryImages,
    walkthroughs: [],
    digitalTwinSurveys: [],
    historyEvents: [],
    surveys: [],
    referenceImages: [],
    twinReadiness: deriveTwinReadiness({
      hasStoreIdentity: Boolean(store.name && store.address && retailerName),
      hasFloorplanImage: false,
      aisleCount: 0,
      zoneCount: 0,
      evidenceCount: mappedGalleryImages.length,
      pendingSuggestionCount: 0,
      unmappedEvidenceCount: 0,
      twinPublishedAt: null,
    }),
    twinPublishedAt: null,
    twinPublishedByName: null,
    unmappedEvidence: [],
    pendingSuggestions: [],
    completenessChecklist: [
      { id: 'live-1', label: 'Store record in REACTOR', complete: true, category: 'operational' },
      { id: 'live-2', label: 'Storefront / store photo', complete: Boolean(store.photoUrl), category: 'visual' },
      { id: 'live-3', label: 'Installation evidence in gallery', complete: galleryImages.length > 0, category: 'visual' },
      { id: 'live-4', label: 'Floorplan survey', complete: false, category: 'physical' },
      { id: 'live-5', label: 'Media location inventory', complete: false, category: 'media' },
    ],
    recentActivity: galleryImages.slice(0, 5).map((img) => ({
      id: `gallery-act-${img.id}`,
      date: img.createdAt.slice(0, 10),
      title: img.campaignName ?? 'Installation evidence',
      description: `Photo captured by ${img.operatorName ?? 'operator'}`,
      type: 'survey' as const,
    })),
  };
}

/**
 * Live store detail — built from the REACTOR store record and live context only.
 * Demo scaffolds are never merged into live stores; demo profiles are reachable
 * only via their slug (see mock-data/profile-overlays.ts).
 */
export function buildDetailFromStore(
  store: ApiStoreRecord,
  retailerName: string,
  formatName: string | null,
  galleryImages: GalleryImageRecord[],
  liveContext?: LiveStoreContext,
  retailerLogoUrl: string | null = null,
): StoreProfileDetail {
  const reactorCore = buildReactorStoreCore(store, retailerName, formatName, galleryImages, retailerLogoUrl);
  return applyLiveStoreContext(reactorCore, liveContext);
}
